import React, { useState, useEffect } from 'react';
import axios from '../../config/axiosConfig';
import { MdArrowBack, MdCloudUpload, MdCheckCircle, MdError, MdDelete, MdSave } from 'react-icons/md';

const EditGallery = ({ galleryId, onBack, onUpdate }) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [date, setDate] = useState('');
    const [currentImages, setCurrentImages] = useState([]);
    const [newImages, setNewImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState(null); // 'success' | 'error' | null

    useEffect(() => {
        fetchGalleryDetails();
    }, [galleryId]);

    const fetchGalleryDetails = async () => {
        try {
            const response = await axios.get(`nai-gallery/${galleryId}/`);
            const data = response.data;
            setTitle(data.title);
            setDescription(data.description);
            // Format date for input type="date"
            const formattedDate = data.created_at ? data.created_at.split('T')[0] : '';
            setDate(formattedDate);
            setCurrentImages(data.images);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching gallery details:", error);
            setLoading(false);
        }
    };

    const handleImageChange = (e) => {
        if (e.target.files) {
            setNewImages((prev) => [...prev, ...Array.from(e.target.files)]);
        }
    };

    const removeNewImage = (index) => {
        setNewImages((prev) => prev.filter((_, i) => i !== index));
    };

    const deleteExistingImage = async (imageId) => {
        if (!window.confirm("Are you sure you want to delete this image?")) return;
        
        try {
            await axios.delete(`nai-gallery/image/${imageId}/delete/`);
            // Remove from local state
            setCurrentImages((prev) => prev.filter((img) => img.id !== imageId));
        } catch (error) {
            console.error("Error deleting image:", error);
            alert("Failed to delete image.");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setStatus(null);

        // 1. Update Gallery Details (Title, Desc, Date)
        try {
            const galleryData = {
                title: title,
                description: description,
                created_at: date ? new Date(date).toISOString() : null
            };

            await axios.put(`nai-gallery/${galleryId}/`, galleryData);

            // 2. Upload New Images if any
            if (newImages.length > 0) {
                const imagesData = new FormData();
                newImages.forEach((image) => {
                    imagesData.append('images', image);
                });
                await axios.post(`nai-gallery/${galleryId}/add-images/`, imagesData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }

            setStatus('success');
            setNewImages([]);
            // Refresh details to see new images
            fetchGalleryDetails();
            setSaving(false);
            if (onUpdate) onUpdate();
        } catch (error) {
            console.error("Error updating gallery:", error);
            setStatus('error');
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="p-6">Loading...</div>;
    }

    return (
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-4xl mx-auto">
            <div className="flex items-center mb-6">
                <button 
                    onClick={onBack}
                    className="mr-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
                >
                    <MdArrowBack className="text-gray-600 w-6 h-6" />
                </button>
                <h2 className="text-2xl font-bold text-gray-800">Edit Gallery</h2>
            </div>

            {status === 'success' && (
                <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 rounded-md flex items-center">
                    <MdCheckCircle className="text-green-500 w-6 h-6 mr-3" />
                    <div>
                        <p className="font-medium text-green-700">Success!</p>
                        <p className="text-sm text-green-600">Gallery updated successfully.</p>
                    </div>
                </div>
            )}

            {status === 'error' && (
                 <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-md flex items-center">
                     <MdError className="text-red-500 w-6 h-6 mr-3" />
                     <div>
                        <p className="font-medium text-red-700">Error</p>
                        <p className="text-sm text-red-600">Failed to update gallery. Please try again.</p>
                     </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">
                {/* Details Section */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">Gallery Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Title</label>
                            <input
                                type="text"
                                required
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Date</label>
                             <input
                                type="date"
                                required
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Description</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2"
                        />
                    </div>
                </div>

                {/* Existing Images Section */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">Existing Images ({currentImages.length})</h3>
                    {currentImages.length === 0 ? (
                        <p className="text-gray-500 italic">No images in this gallery.</p>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            {currentImages.map((img) => (
                                <div key={img.id} className="relative group rounded-lg overflow-hidden border bg-gray-100">
                                    <img 
                                        src={img.image} 
                                        alt="Gallery Item" 
                                        className="w-full h-32 object-cover"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => deleteExistingImage(img.id)}
                                        className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full shadow hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                        title="Delete Image"
                                    >
                                        <MdDelete size={16} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Add New Images Section */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">Add More Images</h3>
                     <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-blue-400 transition-colors">
                        <div className="space-y-1 text-center">
                            <MdCloudUpload className="mx-auto h-12 w-12 text-gray-400" />
                            <div className="flex text-sm text-gray-600">
                                <label htmlFor="new-file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                                    <span>Upload files</span>
                                    <input 
                                        id="new-file-upload" 
                                        name="new-file-upload" 
                                        type="file" 
                                        className="sr-only" 
                                        onChange={handleImageChange} 
                                        accept="image/*" 
                                        multiple 
                                    />
                                </label>
                                <p className="pl-1">or drag and drop</p>
                            </div>
                            <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                        </div>
                    </div>
                    {/* New Images Preview */}
                     {newImages.length > 0 && (
                        <div className="mt-4 grid grid-cols-4 md:grid-cols-6 gap-4">
                            {newImages.map((file, index) => (
                                <div key={index} className="relative group">
                                    <div className="w-full h-20 bg-blue-50 rounded-lg border border-blue-200 flex items-center justify-center p-1 text-xs text-center break-all overflow-hidden text-blue-700">
                                        {file.name}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeNewImage(index)}
                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600"
                                    >
                                        <MdDelete className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex justify-end pt-4 border-t">
                    <button
                        type="submit"
                        disabled={saving}
                        className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${saving ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                        <MdSave className="mr-2" />
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EditGallery;
